import React, { useState, useCallback, useRef } from "react";
import { Download, Printer, Eye, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import DocumentForm from "@/components/DocumentForm";
import PreviewContent from "@/components/preview/PreviewContent";
import TextImprover from "@/components/TextImprover";

function generateNumber(type) {
  var now = new Date();
  var y = now.getFullYear();
  var m = String(now.getMonth() + 1).padStart(2, '0');
  var r = String(Math.floor(Math.random() * 9999)).padStart(4, '0');
  return (type === "invoice" ? "FAC-" : "PRES-") + y + m + "-" + r;
}

function createDoc(type) {
  return {
    type: type,
    number: generateNumber(type),
    date: new Date().toISOString().split('T')[0],
    dueDate: "",
    company: { name: "", address: "", city: "", postalCode: "", cif: "", phone: "", email: "" },
    client: { name: "", address: "", city: "", postalCode: "", cif: "", phone: "", email: "" },
    items: [{ id: 1, description: "", details: "", quantity: 1, unitPrice: 0, discount: 0 }],
    notes: "",
    paymentTerms: "",
    taxRate: 21,
    irpfRate: 0
  };
}

function InvoicePage({ logoUrl, clients, vehicles }) {
  var _useState = useState(function() { return createDoc("invoice"); });
  var doc = _useState[0];
  var setDoc = _useState[1];

  var _useState2 = useState(false);
  var showPreview = _useState2[0];
  var setShowPreview = _useState2[1];

  var _useState3 = useState(false);
  var showTextImprover = _useState3[0];
  var setShowTextImprover = _useState3[1];

  var _useState4 = useState("");
  var textToImprove = _useState4[0];
  var setTextToImprove = _useState4[1];

  var _useState5 = useState(null);
  var textTarget = _useState5[0];
  var setTextTarget = _useState5[1];

  var _useState6 = useState("");
  var selectedClientId = _useState6[0];
  var setSelectedClientId = _useState6[1];

  var previewRef = useRef(null);

  function handleChange(updates) {
    setDoc(function(prev) { return { ...prev, ...updates }; });
  }

  function handleTypeChange(type) {
    setDoc(createDoc(type));
  }

  function handleImproveText(text, target) {
    setTextToImprove(text);
    setTextTarget(target);
    setShowTextImprover(true);
  }

  function handleTextImproved(improved) {
    if (textTarget === "notes") {
      setDoc(function(prev) { return { ...prev, notes: improved }; });
    } else if (textTarget === "paymentTerms") {
      setDoc(function(prev) { return { ...prev, paymentTerms: improved }; });
    } else if (textTarget && textTarget.startsWith("item-details-")) {
      var id = parseInt(textTarget.split("-")[2]);
      setDoc(function(prev) {
        return { ...prev, items: prev.items.map(function(i) { return i.id === id ? { ...i, details: improved } : i; }) };
      });
    } else if (textTarget && textTarget.startsWith("item-")) {
      var id2 = parseInt(textTarget.split("-")[1]);
      setDoc(function(prev) {
        return { ...prev, items: prev.items.map(function(i) { return i.id === id2 ? { ...i, description: improved } : i; }) };
      });
    }
    setShowTextImprover(false);
  }

  function handleClientSelect(clientId) {
    setSelectedClientId(clientId);
    var client = clients.find(function(c) { return c.id === parseInt(clientId); });
    if (client) {
      setDoc(function(prev) {
        return { ...prev, client: { ...prev.client, name: client.name, phone: client.phone } };
      });
    }
  }

  function handleDownloadPDF() {
    if (!previewRef.current) return;
    toast.loading("Generando PDF...", { id: "pdf" });
    html2canvas(previewRef.current, { scale: 2, useCORS: true, backgroundColor: "#fff" })
      .then(function(canvas) {
        var pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, 210, (canvas.height * 210) / canvas.width);
        pdf.save((doc.type === "invoice" ? "Factura_" : "Presupuesto_") + doc.number + ".pdf");
        toast.success("PDF descargado", { id: "pdf" });
        setShowPreview(false);
      })
      .catch(function() { toast.error("Error al generar PDF", { id: "pdf" }); });
  }

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-slate-50" data-testid="invoice-page">
      {/* Header */}
      <div className="p-6 bg-white border-b">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Presupuestos y Facturas</h1>
            <p className="text-slate-500">Crear y gestionar documentos</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">Tipo:</Label>
              <Select value={doc.type} onValueChange={handleTypeChange}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Factura</SelectItem>
                  <SelectItem value="quote">Presupuesto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">Cliente CRM:</Label>
              <Select value={selectedClientId} onValueChange={handleClientSelect}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(function(c) {
                    return <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={function() { setShowPreview(true); }} className="btn-primary gap-2">
              <Eye size={18} /> Vista Previa
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        <div className="w-1/2 p-6 overflow-y-auto">
          <DocumentForm document={doc} onChange={handleChange} onImproveText={handleImproveText} />
        </div>
        <div className="w-1/2 bg-slate-200 p-6 overflow-y-auto">
          <div className="transform scale-[0.5] origin-top-left" style={{ width: "200%" }}>
            <PreviewContent innerRef={null} document={doc} logoUrl={logoUrl} />
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden p-0">
          <div className="sticky top-0 z-10 bg-white border-b px-6 py-4 flex justify-between items-center">
            <h2 className="text-lg font-bold">Vista Previa</h2>
            <div className="flex gap-3">
              <Button onClick={function() { window.print(); }} variant="outline" className="gap-2">
                <Printer size={18} /> Imprimir
              </Button>
              <Button onClick={handleDownloadPDF} className="btn-primary gap-2">
                <Download size={18} /> Descargar PDF
              </Button>
              <Button variant="ghost" size="sm" onClick={function() { setShowPreview(false); }} className="h-10 w-10 p-0">
                <X size={20} />
              </Button>
            </div>
          </div>
          <div className="overflow-y-auto p-6 bg-slate-100" style={{ maxHeight: "calc(95vh - 80px)" }}>
            <div className="mx-auto" style={{ maxWidth: "210mm" }}>
              <PreviewContent innerRef={previewRef} document={doc} logoUrl={logoUrl} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Text Improver */}
      <TextImprover 
        open={showTextImprover} 
        onClose={function() { setShowTextImprover(false); }} 
        text={textToImprove} 
        onApply={handleTextImproved} 
      />
    </div>
  );
}

export default InvoicePage;
